import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo } from 'react';
import { buildLeadAgentsQuery } from '@/views/Interactions/hooks/useInteractionsAgents/buildAgentsQuery';
import {
  hasNextPage,
  type InteractionsAgent,
} from '@/views/Interactions/hooks/useInteractionsAgents/helpers';
import { useIdentity } from '$api-clients/hooks';
import { useAppFlags } from '$launch-darkly/app-flags';

/**
 * The teams a lead reaches: every ACTIVE LEAD membership plus the teams nested
 * beneath. Mirrors the backend's lead-reach query exactly, so the options match
 * what it grants.
 */
export function useLeadTeamIds({ skip = false }: { skip?: boolean } = {}) {
  const { txmLeadTransactionAccess } = useAppFlags();
  const { data: userinfo } = useIdentity('/auth/userinfo');
  const userId = userinfo?.id;

  const isLead = Boolean(
    userinfo?.teams.some((team) => team.roles.includes('LEAD')),
  );

  const { data, isLoading, error } = useIdentity(
    '/users/{userId}/team-hierarchy',
    !skip && txmLeadTransactionAccess && userId && isLead
      ? {
          params: {
            path: { userId },
            query: {
              anchorOn: 'role[eq]=LEAD & status[eq]=ACTIVE',
              expandDownlineOn: 'role[eq]=LEAD',
            },
          },
        }
      : null,
    { revalidateOnFocus: false },
  );

  const teamIds = useMemo(
    () => (data ? Object.keys(data.data.nodes).sort() : []),
    [data],
  );

  return { userId, teamIds, loading: isLoading, error };
}

interface LeadAgentsParams {
  limit: number;
  skip?: boolean;
  searchValue?: string;
  keepPreviousData: boolean;
  memberQuery?: string;
}

export function useLeadAgents({
  limit,
  skip = false,
  searchValue,
  keepPreviousData,
  memberQuery = 'status[nin]=[DRAFT]',
}: LeadAgentsParams) {
  const lead = useLeadTeamIds({ skip });

  const { data, isLoading, error } = useIdentity(
    '/team-members',
    !skip && lead.teamIds.length
      ? {
          params: {
            query: {
              limit,
              q: buildLeadAgentsQuery({
                teamIds: lead.teamIds,
                searchValue,
                memberQuery,
              }),
            },
          },
        }
      : null,
    { revalidateOnFocus: false, keepPreviousData },
  );

  const agents: InteractionsAgent[] = useMemo(
    () =>
      (data?.data ?? []).map((member) => ({
        id: member.legacyExternalId ?? member.userId,
        userId: member.userId,
        firstName: member.firstName,
        lastName: member.lastName,
        teamId: member.team.legacyExternalId ?? member.team.id,
        teamName: member.team.alias,
      })),
    [data],
  );

  return {
    agents,
    truncated: hasNextPage(data),
    loading: lead.loading || isLoading,
    error: lead.error ?? error,
  };
}

// Hierarchy nodes carry no `legacyExternalId`, which is the id space team
// facets live in, so the teams are fetched to get it.
export function useLeadTeams() {
  const lead = useLeadTeamIds();

  const { data, isLoading, error } = useIdentity(
    '/teams',
    lead.teamIds.length
      ? { params: { query: { ids: lead.teamIds.join(','), limit: 500 } } }
      : null,
    { revalidateOnFocus: false },
  );

  const overflowed = hasNextPage(data);

  useEffect(() => {
    if (!overflowed) return;

    datadogRum.addAction('Interactions lead teams exceeded one page', {
      limit: 500,
      userId: lead.userId,
    });
  }, [overflowed, lead.userId]);

  const teams = useMemo(
    () =>
      (data?.data ?? []).map(({ id, alias, legacyExternalId }) => ({
        id,
        alias,
        legacyExternalId,
      })),
    [data],
  );

  const teamIds = useMemo(
    () =>
      new Set(
        teams.flatMap(({ id, legacyExternalId }) =>
          legacyExternalId ? [id, legacyExternalId] : [id],
        ),
      ),
    [teams],
  );

  return {
    teams,
    teamIds,
    loading: lead.loading || isLoading,
    error: lead.error ?? error,
  };
}
